import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BrainSignalLevel,
  DoBoxEntryKind,
  InboxEntry,
} from '@this+that/models';
import type { TFunction } from 'i18next';
import log from 'loglevel';

import type { BrainSignalHistoryEntryView } from '../../models/brainSignals';
import { BRAIN_SIGNAL_LEVEL_KEY } from '../../pages/private/Team/components/Detail/components/BrainPanel/brainPanel.constants';
import { flattenEntityTree } from '../../pages/private/Team/components/Detail/components/BrainPanel/utils/brainEntityTree';
import { resolveSignalName } from '../../pages/private/Team/components/Detail/components/BrainPanel/utils/brainSignals';
import { buildBrainSignalPath } from '../../routes/constants';
import {
  useGetBrainSignal,
  useGetBrainSignalDefinitions,
} from '../../services/brainSignals.service';
import { useGetBrainEntityTree } from '../../services/companyBrain.service';

export enum BrainSignalReviewLoadState {
  IDLE = 'idle',
  LOADING = 'loading',
  READY = 'ready',
  UNAVAILABLE = 'unavailable',
  ERROR = 'error',
}

export interface BrainSignalReviewLabels {
  loading: string;
  error: string;
  unavailable: string;
  unavailableHint: string;
  scope: string;
  reasons: string;
  rationale: string;
  facts: string;
  openSignal: string;
}

export interface BrainSignalReviewDetails {
  name: string;
  level: BrainSignalLevel;
  levelLabel: string;
  scopeLabel: string | null;
  reasonCodes: string[];
  rationale: string | null;
  citedAssertionIds: string[];
  signalPath: string;
}

export interface BrainSignalReviewEntryState {
  isBrainSignalReview: boolean;
  teamId: string;
  loadState: BrainSignalReviewLoadState;
  details: BrainSignalReviewDetails | null;
  labels: BrainSignalReviewLabels;
}

interface UseBrainSignalReviewEntryOptions {
  includeScopeEntity?: boolean;
}

type LoadedSignal = Omit<BrainSignalReviewDetails, 'name' | 'levelLabel'> & {
  name: ReturnType<typeof resolveSignalName>;
};

function translateName(name: LoadedSignal['name'], t: TFunction): string {
  if (!name) {
    return t('brain.signals.unknownDefinition');
  }
  return 'text' in name ? name.text : t(name.key);
}

function buildLabels(t: TFunction): BrainSignalReviewLabels {
  return {
    loading: t('brain.signals.detail.loading'),
    error: t('doBox.brainSignalReview.loadError'),
    unavailable: t('doBox.brainSignalReview.unavailable'),
    unavailableHint: t('doBox.brainSignalReview.unavailableHint'),
    scope: t('brain.signals.detail.scope'),
    reasons: t('doBox.brainSignalReview.reasons'),
    rationale: t('doBox.brainSignalReview.rationale'),
    facts: t('brain.signals.detail.facts'),
    openSignal: t('doBox.brainSignalReview.openSignal'),
  };
}

export function pickLatestHistoryEntry(
  history: readonly BrainSignalHistoryEntryView[],
): BrainSignalHistoryEntryView | null {
  return history.reduce<BrainSignalHistoryEntryView | null>(
    (latest, entry) =>
      !latest || entry.version > latest.version ? entry : latest,
    null,
  );
}

export function useBrainSignalReviewEntry(
  inboxEntry: Pick<InboxEntry, 'entryKind' | 'brainSignalReview'>,
  { includeScopeEntity = true }: UseBrainSignalReviewEntryOptions = {},
): BrainSignalReviewEntryState {
  const { t } = useTranslation();
  const { getBrainSignal } = useGetBrainSignal();
  const { getBrainSignalDefinitions } = useGetBrainSignalDefinitions();
  const { getBrainEntityTree } = useGetBrainEntityTree();
  const isBrainSignalReview =
    inboxEntry.entryKind === DoBoxEntryKind.BRAIN_SIGNAL_REVIEW;
  const teamId = inboxEntry.brainSignalReview?.teamId ?? '';
  const signalId = inboxEntry.brainSignalReview?.signalId ?? '';
  const canLoad = isBrainSignalReview && Boolean(teamId && signalId);
  const [loadState, setLoadState] = useState(
    canLoad
      ? BrainSignalReviewLoadState.LOADING
      : BrainSignalReviewLoadState.IDLE,
  );
  const [loaded, setLoaded] = useState<LoadedSignal | null>(null);

  useEffect(() => {
    if (!canLoad) {
      return undefined;
    }
    let cancelled = false;
    setLoadState(BrainSignalReviewLoadState.LOADING);
    Promise.all([
      getBrainSignal(teamId, signalId),
      getBrainSignalDefinitions(teamId),
      includeScopeEntity
        ? getBrainEntityTree(teamId, null).catch(() => [])
        : Promise.resolve([]),
    ])
      .then(([detail, definitions, tree]) => {
        if (cancelled) {
          return;
        }
        if (!detail) {
          setLoaded(null);
          setLoadState(BrainSignalReviewLoadState.UNAVAILABLE);
          return;
        }
        const latest = pickLatestHistoryEntry(detail.history);
        const scopeEntity = flattenEntityTree(tree).find(
          ({ entityId }) => entityId === detail.signal.scopeEntityId,
        );
        setLoaded({
          name: resolveSignalName(detail.signal, definitions),
          level: detail.signal.level,
          scopeLabel: scopeEntity?.canonicalName ?? null,
          reasonCodes: latest?.reasonCodes ?? [],
          rationale: latest?.rationale?.trim() || null,
          citedAssertionIds: detail.signal.citedAssertionIds,
          signalPath: buildBrainSignalPath(teamId, signalId),
        });
        setLoadState(BrainSignalReviewLoadState.READY);
      })
      .catch((error) => {
        if (!cancelled) {
          log.error('useBrainSignalReviewEntry: load failed', error);
          setLoadState(BrainSignalReviewLoadState.ERROR);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    canLoad,
    teamId,
    signalId,
    includeScopeEntity,
    getBrainSignal,
    getBrainSignalDefinitions,
    getBrainEntityTree,
  ]);

  const details = loaded && {
    ...loaded,
    name: translateName(loaded.name, t),
    levelLabel: t(BRAIN_SIGNAL_LEVEL_KEY[loaded.level]),
  };

  return {
    isBrainSignalReview,
    teamId,
    loadState,
    details: loadState === BrainSignalReviewLoadState.READY ? details : null,
    labels: buildLabels(t),
  };
}
